import { Injectable } from '@angular/core';
import { Cordova, IonicNativePlugin, Plugin } from '@ionic-native/core';

/**
 * Subset of TrueLayer environments exposed via the Cordova bridge.
 * Mirrors the enum names used on both native sides.
 */
export type TrueLayerEnvironment = 'sandbox' | 'production';

/** Configure options passed to {@link TrueLayerPlugin.configure}. */
export interface ITrueLayerConfigureOptions {
  environment: TrueLayerEnvironment;
}

/** Payload for {@link TrueLayerPlugin.processSinglePayment}. */
export interface ITrueLayerSinglePaymentPayload {
  paymentId: string;
  resourceToken: string;
  /**
   * Redirect URI configured in the TrueLayer Console.
   * Must match the deep-link handled by `TrueLayerRedirectActivity` on Android
   * and the universal link on iOS — e.g. `com.ria.RIAMoneyTransfer://truelayer-redirect`.
   */
  redirectUri: string;
  preferences?: {
    preferredCountryCode?: string;
  };
}

/** Payload for {@link TrueLayerPlugin.paymentStatus}. */
export interface ITrueLayerPaymentStatusPayload {
  paymentId: string;
  resourceToken: string;
}

/** Result shape returned by the native side on success. */
export interface ITrueLayerSuccess {
  ok: true;
  status: 'authorized' | 'executed' | 'settled' | 'redirect' | 'wait';
  step?: string;
}

/** Result shape returned by the native side on failure. */
export interface ITrueLayerFailure {
  ok: false;
  reason: string;
  message?: string;
}

/**
 * Ionic Native wrapper for `cordova-plugin-truelayer`.
 *
 * Native counterparts:
 *   - iOS:     `scripts/plugin/cordova-plugin-truelayer/src/ios/TrueLayerPlugin.swift`
 *   - Android: `scripts/plugin/cordova-plugin-truelayer/src/android/TrueLayerPlugin.kt`
 *
 * Usage example (typically from a payment flow):
 *
 * ```ts
 * await this.trueLayerPlugin.configure({ environment: 'sandbox' });
 * const res = await this.trueLayerPlugin.processSinglePayment({
 *   paymentId: '...',
 *   resourceToken: '...',
 *   redirectUri: 'com.ria.RIAMoneyTransfer://truelayer-redirect',
 * });
 * ```
 *
 * TODO(TRUELAYER-CONFIG): replace the `redirectUri` and environment with values
 * from `APP_CONFIG` once the TrueLayer Console registration is finalized.
 */
@Plugin({
  pluginName: 'TrueLayerPlugin',
  plugin: 'cordova-plugin-truelayer',
  pluginRef: 'TrueLayerPlugin',
  platforms: ['Android', 'iOS'],
})
@Injectable()
export class TrueLayerPlugin extends IonicNativePlugin {

  @Cordova({ otherPromise: true })
  configure(_opts: ITrueLayerConfigureOptions): Promise<ITrueLayerSuccess> {
    return;
  }

  @Cordova({ otherPromise: true })
  processSinglePayment(_payload: ITrueLayerSinglePaymentPayload): Promise<ITrueLayerSuccess> {
    return;
  }

  @Cordova({ otherPromise: true })
  paymentStatus(_payload: ITrueLayerPaymentStatusPayload): Promise<ITrueLayerSuccess> {
    return;
  }
}



// WEBPACK FOOTER //
// ./src/providers/truelayer/truelayer-sdk.ts